// Outpatient dispensing (docs/plans/phase1-dispensing.md §5.3). Prescriptions and
// dispenses are addressed by UUID only; the store is not patient data.
import { queryOptions } from '@tanstack/react-query';
import {
  dispenseViewSchema,
  dispensingViewSchema,
  labelsViewSchema,
  pharmacyQueueSchema,
  type cancelDispenseBodySchema,
  type closeBalanceBodySchema,
  type createDispenseBodySchema,
  type dispenseBodySchema,
} from '@wodi/shared';
import type { z } from 'zod';

import { api } from './client.ts';

type In<T extends z.ZodType> = z.input<T>;

export const dispensingKeys = {
  all: ['pharmacy', 'dispensing'] as const,
  queue: ['pharmacy', 'dispensing', 'queue'] as const,
  work: (prescriptionId: string, storeId: string) =>
    ['pharmacy', 'dispensing', 'work', prescriptionId, storeId] as const,
  labels: (dispenseId: string) => ['pharmacy', 'dispensing', 'labels', dispenseId] as const,
  dispense: (id: string) => ['pharmacy', 'dispensing', 'dispense', id] as const,
};

export const pharmacyQueueQuery = queryOptions({
  queryKey: dispensingKeys.queue,
  queryFn: () => api('GET', '/pharmacy/queue', { schema: pharmacyQueueSchema }),
});

export const dispensingQuery = (prescriptionId: string, storeId: string) =>
  queryOptions({
    queryKey: dispensingKeys.work(prescriptionId, storeId),
    queryFn: () =>
      api('GET', `/prescriptions/${prescriptionId}/dispensing?storeId=${storeId}`, {
        schema: dispensingViewSchema,
      }),
  });

export const dispenseQuery = (id: string) =>
  queryOptions({
    queryKey: dispensingKeys.dispense(id),
    queryFn: () => api('GET', `/dispenses/${id}`, { schema: dispenseViewSchema }),
  });

export const labelsQuery = (dispenseId: string) =>
  queryOptions({
    queryKey: dispensingKeys.labels(dispenseId),
    queryFn: () => api('GET', `/dispenses/${dispenseId}/labels`, { schema: labelsViewSchema }),
  });

export const createDispense = (body: In<typeof createDispenseBodySchema>) =>
  api('POST', '/dispenses', { body, schema: dispenseViewSchema });

export const dispensePrepared = (id: string, body: In<typeof dispenseBodySchema>) =>
  api('POST', `/dispenses/${id}/dispense`, { body, schema: dispenseViewSchema });

export const cancelDispense = (id: string, body: In<typeof cancelDispenseBodySchema>) =>
  api('POST', `/dispenses/${id}/cancel`, { body, schema: dispenseViewSchema });

export const closeBalance = (lineId: string, body: In<typeof closeBalanceBodySchema>) =>
  api('POST', `/prescription-lines/${lineId}/close-balance`, { body });

export const recordCounselling = (dispenseId: string, note: string) =>
  api('POST', `/dispenses/${dispenseId}/counselling-notes`, {
    body: { note },
    schema: dispenseViewSchema,
  });

export const markLabelsPrinted = (dispenseId: string, lineIds: string[]) =>
  api('POST', `/dispenses/${dispenseId}/labels/printed`, {
    body: { lineIds },
    schema: labelsViewSchema,
  });
